import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { Search, MapPin, X, Loader2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Component to handle map clicks
const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// Component to recenter map
const RecenterMap = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (lat && lng) {
      map.setView([lat, lng], 15);
    }
  }, [lat, lng, map]);
  return null;
};

const LocationPicker = ({
  latitude,
  longitude,
  radius = 100,
  onLocationChange,
  onRadiusChange,
  className = "",
  showMapByDefault = true,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showMap, setShowMap] = useState(showMapByDefault);
  const [selectedAddress, setSelectedAddress] = useState('');
  const searchTimeoutRef = useRef(null);
  const mapRef = useRef(null);

  // Default to South Africa if no coordinates
  const defaultLat = latitude || -26.2041;
  const defaultLng = longitude || 28.0473;

  // Search for address using Nominatim (OpenStreetMap)
  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=za&limit=5`,
        {
          headers: {
            'Accept-Language': 'en',
          },
        }
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(searchQuery);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Reverse geocode to get address
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
        {
          headers: {
            'Accept-Language': 'en',
          },
        }
      );
      const data = await response.json();
      if (data.display_name) {
        setSelectedAddress(data.display_name);
      }
    } catch (error) {
      console.error('Reverse geocode failed:', error);
    }
  };

  const handleSelectResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    onLocationChange(lat, lng);
    setSelectedAddress(result.display_name);
    setSearchQuery('');
    setSearchResults([]);
    // Ensure map is shown and centered
    setShowMap(true);
  };

  const handleMapClick = (lat, lng) => {
    onLocationChange(lat, lng);
    reverseGeocode(lat, lng);
  };

  const handleClearLocation = () => {
    onLocationChange(null, null);
    setSelectedAddress('');
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search Input */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Search Address
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for an address..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {searching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
          )}
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50 relative">
            {searchResults.map((result, index) => (
              <button
                key={index}
                onClick={() => handleSelectResult(result)}
                className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm border-b border-slate-100 last:border-b-0"
              >
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{result.display_name}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected Location Display */}
      {latitude && longitude && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Selected Location</p>
                {selectedAddress && (
                  <p className="text-xs text-blue-700 mt-0.5">{selectedAddress}</p>
                )}
                <p className="text-xs text-blue-600 mt-1">
                  {latitude.toFixed(6)}, {longitude.toFixed(6)}
                </p>
              </div>
            </div>
            <button
              onClick={handleClearLocation}
              className="p-1 hover:bg-blue-100 rounded"
            >
              <X className="w-4 h-4 text-blue-600" />
            </button>
          </div>
        </div>
      )}

      {/* Map Toggle */}
      <button
        type="button"
        onClick={() => setShowMap(!showMap)}
        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
      >
        <MapPin className="w-4 h-4" />
        {showMap ? 'Hide map' : 'Or select on map'}
      </button>

      {/* Map */}
      {showMap && (
        <div className="border border-slate-200 rounded-lg overflow-hidden" style={{ height: '300px' }}>
          <MapContainer
            center={[latitude || defaultLat, longitude || defaultLng]}
            zoom={latitude ? 15 : 10}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapClickHandler onLocationSelect={handleMapClick} />
            {latitude && longitude && (
              <>
                <Marker position={[latitude, longitude]} />
                <RecenterMap lat={latitude} lng={longitude} />
              </>
            )}
          </MapContainer>
          <p className="text-xs text-slate-500 p-2 bg-slate-50">
            Click on the map to set the geofence location
          </p>
        </div>
      )}

      {/* Radius Input */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Geofence Radius (meters)
        </label>
        <input
          type="number"
          value={radius}
          onChange={(e) => onRadiusChange(parseInt(e.target.value) || 100)}
          min="10"
          max="5000"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="text-xs text-slate-500 mt-1">
          Recommended: 50-200m for buildings, 200-500m for large sites
        </p>
      </div>
    </div>
  );
};

export default LocationPicker;
